// Suggestion lists for the inline audience builder. Fixed-value lists must match
// SIZE_VALUES / SENIORITY_VALUES / DEPARTMENT_VALUES in the company-profile-builder edge function.

export interface Opt { value: string; label: string }

const POPULAR = ["India", "United States", "United Kingdom", "UAE", "Saudi Arabia", "Singapore", "Canada", "Australia", "Germany", "Qatar"];
const OTHER_COUNTRIES = [
  "Argentina", "Austria", "Bahrain", "Bangladesh", "Belgium", "Brazil", "Bulgaria", "Chile", "China", "Colombia", "Croatia",
  "Cyprus", "Czech Republic", "Denmark", "Egypt", "Estonia", "Finland", "France", "Ghana", "Greece", "Hong Kong", "Hungary",
  "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kenya", "Kuwait", "Latvia", "Lithuania", "Luxembourg",
  "Malaysia", "Mauritius", "Mexico", "Morocco", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway", "Oman",
  "Pakistan", "Peru", "Philippines", "Poland", "Portugal", "Romania", "Serbia", "Slovakia", "Slovenia", "South Africa",
  "South Korea", "Spain", "Sri Lanka", "Sweden", "Switzerland", "Taiwan", "Tanzania", "Thailand", "Turkey", "Uganda",
  "Ukraine", "Vietnam",
];
export const COUNTRIES = [...POPULAR, ...OTHER_COUNTRIES];

export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  India: ["Mumbai", "Bangalore", "Delhi", "Pune", "Hyderabad", "Chennai", "Ahmedabad", "Kolkata", "Gurgaon", "Noida", "Jaipur", "Surat", "Indore", "Chandigarh", "Kochi", "Coimbatore", "Vadodara", "Lucknow", "Nagpur"],
  "United States": ["New York", "San Francisco", "Los Angeles", "Chicago", "Austin", "Boston", "Seattle", "Dallas", "Houston", "Atlanta", "Miami", "Denver", "San Jose", "Washington DC", "Philadelphia"],
  "United Kingdom": ["London", "Manchester", "Birmingham", "Edinburgh", "Leeds", "Glasgow", "Bristol", "Liverpool", "Cambridge", "Oxford"],
  UAE: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman"],
  "Saudi Arabia": ["Riyadh", "Jeddah", "Dammam", "Khobar", "Mecca", "Medina"],
  Singapore: ["Singapore"],
  Canada: ["Toronto", "Vancouver", "Montreal", "Calgary", "Ottawa", "Edmonton", "Mississauga"],
  Australia: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Canberra"],
  Germany: ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne", "Stuttgart", "Düsseldorf"],
  Qatar: ["Doha"],
  France: ["Paris", "Lyon", "Marseille", "Toulouse", "Nice"],
  Netherlands: ["Amsterdam", "Rotterdam", "The Hague", "Utrecht", "Eindhoven"],
  Spain: ["Madrid", "Barcelona", "Valencia", "Seville"],
  Italy: ["Milan", "Rome", "Turin", "Bologna"],
  Kuwait: ["Kuwait City"], Bahrain: ["Manama"], Oman: ["Muscat"],
  Malaysia: ["Kuala Lumpur", "Penang", "Johor Bahru"],
  Indonesia: ["Jakarta", "Surabaya", "Bandung"],
  "South Africa": ["Johannesburg", "Cape Town", "Durban", "Pretoria"],
  Nigeria: ["Lagos", "Abuja"], Kenya: ["Nairobi", "Mombasa"],
  Ireland: ["Dublin", "Cork"], Switzerland: ["Zurich", "Geneva", "Basel"],
  Japan: ["Tokyo", "Osaka"], "Hong Kong": ["Hong Kong"], "New Zealand": ["Auckland", "Wellington"],
  Egypt: ["Cairo", "Alexandria"], Turkey: ["Istanbul", "Ankara"], Brazil: ["São Paulo", "Rio de Janeiro"], Mexico: ["Mexico City", "Monterrey", "Guadalajara"],
};
export const ALL_CITIES = Array.from(new Set(Object.values(CITIES_BY_COUNTRY).flat()));

export const INDUSTRIES = [
  "Information Technology & Services", "Software Development", "IT Services and IT Consulting", "Computer Hardware", "Internet",
  "Financial Services", "Banking", "Insurance", "Investment Management", "Venture Capital & Private Equity", "Accounting",
  "Hospitals and Health Care", "Medical Devices", "Pharmaceutical Manufacturing", "Biotechnology", "Wellness and Fitness",
  "Manufacturing", "Industrial Machinery Manufacturing", "Automotive", "Electrical & Electronic Manufacturing", "Chemical Manufacturing",
  "Plastics Manufacturing", "Packaging and Containers Manufacturing", "Textile Manufacturing", "Food and Beverage Manufacturing",
  "Machinery", "Mechanical or Industrial Engineering", "Aviation & Aerospace", "Defense & Space", "Oil and Gas", "Renewable Energy",
  "Utilities", "Mining", "Construction", "Building Materials", "Civil Engineering", "Architecture & Planning", "Real Estate",
  "Retail", "E-commerce", "Consumer Goods", "Apparel & Fashion", "Luxury Goods & Jewelry", "Cosmetics", "Furniture",
  "Food & Beverages", "Restaurants", "Hospitality", "Travel Arrangements", "Leisure, Travel & Tourism", "Events Services",
  "Education", "Higher Education", "E-Learning", "Primary and Secondary Education", "Professional Training and Coaching",
  "Marketing & Advertising", "Public Relations and Communications", "Market Research", "Design Services", "Media Production",
  "Online Media", "Broadcast Media", "Entertainment", "Publishing", "Telecommunications", "Wireless Services",
  "Management Consulting", "Business Consulting and Services", "Staffing and Recruiting", "Human Resources Services",
  "Outsourcing and Offshoring Consulting", "Legal Services", "Law Practice", "Logistics and Supply Chain",
  "Transportation, Logistics, Supply Chain and Storage", "Truck Transportation", "Maritime Transportation", "Warehousing",
  "Import and Export", "Wholesale", "Agriculture", "Farming", "Environmental Services", "Facilities Services",
  "Security and Investigations", "Computer & Network Security", "Government Administration", "Non-profit Organizations",
  "Research Services", "Semiconductors", "Robotics", "Gaming", "Sports", "Photography", "Printing Services",
  "Civic and Social Organizations", "Religious Institutions", "Veterinary Services", "Dairy", "Fisheries", "Tobacco",
];

export const COMPANY_SIZES: Opt[] = [
  { value: "1-10", label: "Startup (1-10)" },
  { value: "11-50", label: "Small (11-50)" },
  { value: "51-200", label: "Medium (51-200)" },
  { value: "201-1000", label: "Large (201-1000)" },
  { value: "1000+", label: "Enterprise (1000+)" },
];

export const SENIORITY: Opt[] = [
  { value: "entry", label: "Entry" },
  { value: "manager", label: "Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP" },
  { value: "c-level", label: "CXO" },
  { value: "founder", label: "Owner / Founder" },
];

export const DEPARTMENTS = ["Sales", "Marketing", "Human Resources", "Finance", "Engineering", "Operations", "Product", "Customer Success", "Business Development", "Executive/C-Suite"];

export const JOB_TITLES_BY_DEPARTMENT: Record<string, string[]> = {
  "Executive/C-Suite": ["CEO", "Founder", "Co-Founder", "Managing Director", "Owner", "Director", "COO", "President"],
  Sales: ["Head of Sales", "VP Sales", "Sales Director", "Sales Manager", "Regional Sales Manager", "Key Account Manager"],
  Marketing: ["CMO", "Head of Marketing", "Marketing Director", "Marketing Manager", "Brand Manager", "Digital Marketing Manager"],
  "Human Resources": ["CHRO", "HR Director", "HR Manager", "Head of Talent Acquisition", "Talent Acquisition Manager", "HR Business Partner"],
  Finance: ["CFO", "Finance Director", "Finance Manager", "Financial Controller", "Head of Accounts"],
  Engineering: ["CTO", "VP Engineering", "Engineering Manager", "Head of Technology", "IT Director", "IT Manager"],
  Operations: ["COO", "Operations Director", "Operations Manager", "Plant Head", "Plant Manager", "Supply Chain Manager", "Procurement Manager", "Purchase Manager"],
  Product: ["CPO", "Head of Product", "Product Director", "Product Manager"],
  "Customer Success": ["Head of Customer Success", "Customer Success Manager", "Customer Experience Manager"],
  "Business Development": ["Head of Business Development", "Business Development Director", "Business Development Manager", "Partnerships Manager"],
};
export const ALL_JOB_TITLES = Array.from(new Set(Object.values(JOB_TITLES_BY_DEPARTMENT).flat()));
